import { useCallback, useState } from 'react';
import {
  Badge,
  Banner,
  BlockStack,
  Box,
  Button,
  Card,
  Checkbox,
  Divider,
  Icon,
  InlineStack,
  Modal,
  Page,
  Select,
  Spinner,
  Tabs,
  Text,
  TextField,
} from '@shopify/polaris';
import {
  DeleteIcon,
  EditIcon,
  NotificationIcon,
  PlusIcon,
} from '@shopify/polaris-icons';
import { useNavigate } from 'react-router-dom';
import { ApiError } from '@/types/api';
import {
  useAbGroups,
  useAudiencePreview,
  useCollectionPicker,
  useCreateGroup,
  useCreateSchedule,
  useCreateTemplate,
  useDeleteGroup,
  useDeleteSchedule,
  useDeleteTemplate,
  useDeviceGroups,
  useDevices,
  useMarkTemplateUsed,
  useNotificationLimits,
  useNotifications,
  useProductPicker,
  useSchedules,
  useSendAbTest,
  useSendNotification,
  useTemplates,
  useTestGroup,
  useTestNotification,
  useToggleSchedule,
  useUpdateGroup,
  type DeviceGroup,
  type DeviceItem,
  type NotificationTemplate,
  type SendResponse,
} from '@/queries/notifications';
import { ComposeTab } from './push/compose/ComposeTab';
import { HistoryTab } from './push/history/HistoryTab';
import { ABResultsInline } from './push/history/ABResultsInline';
import { SchedulesTab } from './push/schedules/SchedulesTab';
import { TemplatesTab } from './push/templates/TemplatesTab';
import { CartsTab } from './push/carts/CartsTab';
import {
  useNotificationFormState,
  getPrimaryActionLabel,
  formatTime,
} from './push/shared/useNotificationFormState';

export function PushNotificationsPage() {
  const navigate = useNavigate();
  const [tab, setTab] = useState(0);

  // Queries / mutations
  const limits = useNotificationLimits();
  const notifications = useNotifications();
  const devices = useDevices();
  const products = useProductPicker();
  const collections = useCollectionPicker();
  const send = useSendNotification();
  const test = useTestNotification();
  const audiencePreview = useAudiencePreview();
  const groups = useDeviceGroups();
  const testGroup = useTestGroup();
  const createGroup = useCreateGroup();
  const updateGroup = useUpdateGroup();
  const deleteGroup = useDeleteGroup();
  const schedules = useSchedules();
  const createSchedule = useCreateSchedule();
  const toggleSchedule = useToggleSchedule();
  const deleteSchedule = useDeleteSchedule();
  const templates = useTemplates();
  const createTemplate = useCreateTemplate();
  const deleteTemplate = useDeleteTemplate();
  const markTemplateUsed = useMarkTemplateUsed();
  const abGroups = useAbGroups();
  const sendAbTest = useSendAbTest();

  // Form state
  const form = useNotificationFormState();
  const titleMax = limits.data?.title_max ?? 200;
  const bodyMax = limits.data?.body_max ?? 1000;

  // UI state (modals, banners)
  const [formErr, setFormErr] = useState<string | null>(null);
  const [okBanner, setOkBanner] = useState<string | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [testOpen, setTestOpen] = useState(false);
  const [testDeviceId, setTestDeviceId] = useState('');
  const [testMode, setTestMode] = useState<'device' | 'group'>('device');
  const [testGroupId, setTestGroupId] = useState('');

  // Save-as-template
  const [saveTplOpen, setSaveTplOpen] = useState(false);
  const [tplName, setTplName] = useState('');
  const [tplErr, setTplErr] = useState<string | null>(null);

  // Group manager
  const [groupManagerOpen, setGroupManagerOpen] = useState(false);
  const [editingGroup, setEditingGroup] = useState<DeviceGroup | null>(null);
  const [groupFormOpen, setGroupFormOpen] = useState(false);
  const [groupName, setGroupName] = useState('');
  const [groupDeviceIds, setGroupDeviceIds] = useState<Set<number>>(new Set());
  const [groupErr, setGroupErr] = useState<string | null>(null);

  // -------------------------------------------------------------------
  // Validation + handlers
  // -------------------------------------------------------------------
  const validate = (): string | null => {
    const s = form.state;
    if (!s.title.trim()) return 'لازم تكتب عنوان للإشعار.';
    if (!s.body.trim()) return 'لازم تكتب نص الإشعار.';
    if (s.targetType !== 'none' && !s.targetValue.trim()) {
      return 'لازم تختار/تكتب قيمة للـ target.';
    }
    if (s.targetType === 'url') {
      const low = s.targetValue.toLowerCase();
      if (!low.startsWith('https://') && !low.startsWith('http://') && !low.startsWith('padelneeds://')) {
        return 'الـ URL لازم يبدأ بـ https:// أو padelneeds://';
      }
    }
    if (s.abEnabled && (!s.titleB.trim() || !s.bodyB.trim())) {
      return 'لازم تكتب title + body للـ Variant B.';
    }
    return null;
  };

  const onSendClick = useCallback(() => {
    setFormErr(null);
    const err = validate();
    if (err) {
      setFormErr(err);
      return;
    }
    audiencePreview.mutate(form.audience);
    setConfirmOpen(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.audience, form.state]);

  const submitSend = () => {
    setConfirmOpen(false);
    const s = form.state;
    const base = form.buildBasePayload();

    if (s.abEnabled && s.sendMode === 'now') {
      sendAbTest.mutate(
        {
          variant_a: { title: base.title, body: base.body },
          variant_b: { title: s.titleB.trim(), body: s.bodyB.trim() },
          split_a: Number(s.splitA) || 50,
          image_url: base.image_url,
          image_crop: base.image_crop,
          image_bg_color: base.image_bg_color,
          target: base.target,
          audience: form.audience,
        },
        {
          onSuccess: (res: { variant_a: { reach: number }; variant_b: { reach: number } }) => {
            setOkBanner(`A/B sent ✓  A: ${res.variant_a.reach} reach · B: ${res.variant_b.reach} reach`);
            setTab(1); // history
          },
          onError: (e: unknown) =>
            setFormErr(e instanceof ApiError ? e.message : 'فشل الإرسال'),
        },
      );
      return;
    }

    if (s.sendMode !== 'now') {
      const fireAt =
        s.sendMode === 'one_time' && s.scheduleAt
          ? Math.floor(new Date(s.scheduleAt).getTime() / 1000)
          : undefined;
      createSchedule.mutate(
        {
          ...base,
          schedule_type: s.sendMode,
          ...(s.sendMode === 'one_time' ? { fire_at: fireAt } : {}),
          ...(s.sendMode === 'recurring' ? { cron_expr: s.cronExpr.trim() } : {}),
          ...(s.sendMode === 'trigger'
            ? {
                trigger_kind: s.triggerKind,
                trigger_params:
                  s.triggerKind === 'install_plus_hours' || s.triggerKind === 'cart_idle'
                    ? { hours: Number(s.triggerHours) || 24 }
                    : {},
              }
            : {}),
        },
        {
          onSuccess: () => {
            setOkBanner(
              s.sendMode === 'one_time'
                ? `تم الجدولة في ${s.scheduleAt}`
                : s.sendMode === 'recurring'
                ? `Schedule دوري شغّال — cron: ${s.cronExpr}`
                : `Trigger schedule شغّال (${s.triggerKind})`,
            );
            setTab(2); // schedules
          },
          onError: (e: unknown) =>
            setFormErr(e instanceof ApiError ? e.message : 'فشل الجدولة'),
        },
      );
      return;
    }

    send.mutate(base, {
      onSuccess: (res: SendResponse) => {
        setOkBanner(`تم الإرسال: ${res.sent} نجح / ${res.failed} فشل (reach: ${res.reach})`);
        setTab(1);
      },
      onError: (e: unknown) =>
        setFormErr(e instanceof ApiError ? e.message : 'فشل الإرسال'),
    });
  };

  const submitTest = () => {
    setFormErr(null);
    const err = validate();
    if (err) {
      setFormErr(err);
      return;
    }
    const payload = form.buildBasePayload();
    const commonPayload = {
      title: payload.title,
      body: payload.body,
      image_url: payload.image_url,
      image_crop: payload.image_crop,
      image_bg_color: payload.image_bg_color,
      target: payload.target,
    };

    if (testMode === 'group') {
      if (!testGroupId) {
        setFormErr('اختار group من القائمة.');
        return;
      }
      testGroup.mutate(
        { ...commonPayload, group_id: Number(testGroupId) },
        {
          onSuccess: (res: SendResponse) => {
            setOkBanner(`Group test: ${res.sent} نجح / ${res.failed} فشل (reach: ${res.reach})`);
            setTestOpen(false);
          },
          onError: (e: unknown) =>
            setFormErr(e instanceof ApiError ? e.message : 'فشل الـ group test'),
        },
      );
      return;
    }

    if (!testDeviceId) {
      setFormErr('اختار device من القائمة.');
      return;
    }
    test.mutate(
      { ...commonPayload, device_id: Number(testDeviceId) },
      {
        onSuccess: (res: SendResponse) => {
          setOkBanner(
            res.sent
              ? `Test sent بنجاح (notification #${res.notification_id})`
              : `الـ test فشل: ${res.failed} ticket error`,
          );
          setTestOpen(false);
        },
        onError: (e: unknown) =>
          setFormErr(e instanceof ApiError ? e.message : 'فشل الـ test'),
      },
    );
  };

  const submitSaveTemplate = () => {
    setTplErr(null);
    const name = tplName.trim();
    const s = form.state;
    if (!name) {
      setTplErr('اكتب اسم للـ template.');
      return;
    }
    if (!s.title.trim() || !s.body.trim()) {
      setTplErr('لازم title + body فاضين قبل الحفظ.');
      return;
    }
    createTemplate.mutate(
      {
        name,
        title: s.title.trim(),
        body: s.body.trim(),
        image_url: s.imageUrl.trim() || undefined,
        image_crop: s.imageCrop,
        image_bg_color: s.imageBgEnabled ? s.imageBgColor : undefined,
        target:
          s.targetType === 'none'
            ? undefined
            : { type: s.targetType, value: s.targetValue.trim() },
        audience: form.audience,
      },
      {
        onSuccess: () => {
          setSaveTplOpen(false);
          setTplName('');
          setOkBanner(`تم حفظ template "${name}"`);
        },
        onError: (e: unknown) =>
          setTplErr(e instanceof ApiError ? e.message : 'فشل الحفظ'),
      },
    );
  };

  const loadTemplateIntoForm = (t: NotificationTemplate) => {
    form.load({
      title: t.title,
      body: t.body,
      imageUrl: t.image_url ?? '',
      imageCrop: t.image_crop,
      imageBgEnabled: Boolean(t.image_bg_color),
      imageBgColor: t.image_bg_color ?? '#FFFFFF',
      targetType: (t.target_type as 'none' | 'product' | 'collection' | 'order' | 'url') ?? 'none',
      targetValue: t.target_value ?? '',
      targetThumbnail: '',
      targetSearch: t.target_value ?? '',
    });
    markTemplateUsed.mutate(t.id);
    setTab(0);
    setOkBanner(`تم تحميل template "${t.name}"`);
  };

  // -------------------------------------------------------------------
  // Group manager helpers
  // -------------------------------------------------------------------
  const openCreateGroup = () => {
    setEditingGroup(null);
    setGroupName('');
    setGroupDeviceIds(new Set());
    setGroupErr(null);
    setGroupFormOpen(true);
  };

  const openEditGroup = (g: DeviceGroup) => {
    setEditingGroup(g);
    setGroupName(g.name);
    setGroupDeviceIds(new Set(g.members.map((m) => m.id)));
    setGroupErr(null);
    setGroupFormOpen(true);
  };

  const toggleDeviceInGroup = (id: number) => {
    setGroupDeviceIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const submitGroup = () => {
    setGroupErr(null);
    const name = groupName.trim();
    if (!name) {
      setGroupErr('اكتب اسم للـ group.');
      return;
    }
    const ids = Array.from(groupDeviceIds);
    if (editingGroup) {
      updateGroup.mutate(
        { id: editingGroup.id, name, device_ids: ids },
        {
          onSuccess: () => setGroupFormOpen(false),
          onError: (e: unknown) => setGroupErr(e instanceof ApiError ? e.message : 'فشل التحديث'),
        },
      );
    } else {
      createGroup.mutate(
        { name, device_ids: ids },
        {
          onSuccess: () => setGroupFormOpen(false),
          onError: (e: unknown) => setGroupErr(e instanceof ApiError ? e.message : 'فشل الإنشاء'),
        },
      );
    }
  };

  // -------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------
  const tabs = [
    { id: 'compose', content: 'Compose', accessibilityLabel: 'Compose' },
    {
      id: 'history',
      content: `History${notifications.data?.total ? ` (${notifications.data.total})` : ''}`,
      accessibilityLabel: 'History',
    },
    {
      id: 'schedules',
      content: `Schedules${schedules.data?.items.length ? ` (${schedules.data.items.length})` : ''}`,
      accessibilityLabel: 'Schedules',
    },
    {
      id: 'templates',
      content: `Templates${templates.data?.items.length ? ` (${templates.data.items.length})` : ''}`,
      accessibilityLabel: 'Templates',
    },
    { id: 'carts', content: 'الكارتات', accessibilityLabel: 'Carts' },
  ];

  const activeDevices = devices.data?.items.filter((d: DeviceItem) => !d.disabled).length ?? 0;
  const primaryLabel = getPrimaryActionLabel(form.state);
  const primaryLoading = send.isPending || createSchedule.isPending || sendAbTest.isPending;

  return (
    <Page
      title="Push notifications"
      backAction={{ content: 'Dashboard', onAction: () => navigate('/dashboard') }}
      titleMetadata={
        <InlineStack gap="100" blockAlign="center">
          <Icon source={NotificationIcon} />
          <Badge tone="info">{`${activeDevices} active devices`}</Badge>
        </InlineStack>
      }
      primaryAction={{
        content: primaryLabel,
        onAction: onSendClick,
        loading: primaryLoading,
        disabled: !form.canSend,
      }}
      secondaryActions={[
        {
          content: 'ابعت Test',
          onAction: () => {
            setFormErr(null);
            const err = validate();
            if (err) {
              setFormErr(err);
              return;
            }
            setTestOpen(true);
          },
          disabled: !form.canSend,
        },
      ]}
      actionGroups={[
        {
          title: 'المزيد',
          actions: [
            {
              content: 'حفظ كـ template',
              onAction: () => setSaveTplOpen(true),
              disabled: !form.canSend,
            },
            {
              content: 'إدارة Test Groups',
              icon: EditIcon,
              onAction: () => setGroupManagerOpen(true),
            },
          ],
        },
      ]}
    >
      <Card padding="0">
        <Tabs tabs={tabs} selected={tab} onSelect={setTab}>
          <Box padding="400">
            {tab === 0 ? (
              <ComposeTab
                form={form}
                titleMax={titleMax}
                bodyMax={bodyMax}
                products={{
                  items: products.data?.items ?? [],
                  isLoading: products.isLoading,
                }}
                collections={{
                  items: collections.data?.items ?? [],
                  isLoading: collections.isLoading,
                }}
                audienceCount={audiencePreview.data?.count ?? null}
                audienceLoading={audiencePreview.isPending}
                onRefreshAudienceCount={() => audiencePreview.mutate(form.audience)}
                formErr={formErr}
                okBanner={okBanner}
                onDismissFormErr={() => setFormErr(null)}
                onDismissOkBanner={() => setOkBanner(null)}
              />
            ) : tab === 1 ? (
              <BlockStack gap="400">
                <HistoryTab
                  notifications={notifications.data?.items ?? []}
                  isLoading={notifications.isLoading}
                  error={notifications.error as Error | null}
                  onGotoCompose={() => setTab(0)}
                />
                {abGroups.data?.items?.length ? (
                  <ABResultsInline groups={abGroups.data.items} />
                ) : null}
              </BlockStack>
            ) : tab === 2 ? (
              <SchedulesTab
                schedules={schedules.data?.items ?? []}
                isLoading={schedules.isLoading}
                onToggle={(id, enabled) => toggleSchedule.mutate({ id, enabled })}
                onDelete={(id) => deleteSchedule.mutate(id)}
                togglePending={toggleSchedule.isPending}
                deletePending={deleteSchedule.isPending}
                onGotoCompose={() => setTab(0)}
              />
            ) : tab === 3 ? (
              <TemplatesTab
                templates={templates.data?.items ?? []}
                isLoading={templates.isLoading}
                onLoad={loadTemplateIntoForm}
                onDelete={(id) => deleteTemplate.mutate(id)}
                deletePending={deleteTemplate.isPending}
                onGotoCompose={() => setTab(0)}
              />
            ) : (
              <CartsTab
                schedules={schedules.data?.items ?? []}
                groups={groups.data?.items ?? []}
              />
            )}
          </Box>
        </Tabs>
      </Card>

      {/* ---- Send confirmation modal -------------------------------- */}
      <Modal
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        title="تأكيد الإرسال"
        primaryAction={{
          content: primaryLabel,
          onAction: submitSend,
          loading: primaryLoading,
        }}
        secondaryActions={[{ content: 'إلغاء', onAction: () => setConfirmOpen(false) }]}
      >
        <Modal.Section>
          <BlockStack gap="200">
            <Text as="p">
              هتبعت notification لـ{' '}
              <strong>{audiencePreview.data?.count ?? '…'}</strong> device.
            </Text>
            <Text as="p" tone="subdued" variant="bodySm">
              "{form.state.title}" — "{form.state.body}"
            </Text>
          </BlockStack>
        </Modal.Section>
      </Modal>

      {/* ---- Send test modal --------------------------------------- */}
      <Modal
        open={testOpen}
        onClose={() => setTestOpen(false)}
        title="ابعت Test"
        primaryAction={{
          content: testMode === 'group' ? 'ابعت للـ Group' : 'ابعت Test',
          onAction: submitTest,
          loading: test.isPending || testGroup.isPending,
        }}
        secondaryActions={[{ content: 'إلغاء', onAction: () => setTestOpen(false) }]}
      >
        <Modal.Section>
          <BlockStack gap="300">
            <Select
              label="ابعت لـ"
              options={[
                { label: 'Device واحد', value: 'device' },
                { label: 'Group (مجموعة devices)', value: 'group' },
              ]}
              value={testMode}
              onChange={(v) => setTestMode(v as 'device' | 'group')}
            />
            {testMode === 'device' ? (
              <Select
                label="اختار device"
                options={[
                  { label: '— اختار —', value: '' },
                  ...(devices.data?.items ?? [])
                    .filter((d: DeviceItem) => !d.disabled)
                    .map((d: DeviceItem) => ({
                      label: `#${d.id} · ${d.platform}/${d.locale ?? '?'} · ${
                        d.customer_id ? 'logged-in' : 'guest'
                      } · ${formatTime(d.last_seen)}`,
                      value: String(d.id),
                    })),
                ]}
                value={testDeviceId}
                onChange={setTestDeviceId}
              />
            ) : (
              <Select
                label="اختار group"
                options={[
                  { label: '— اختار —', value: '' },
                  ...(groups.data?.items ?? []).map((g: DeviceGroup) => ({
                    label: `${g.name} (${g.member_count} device${g.member_count === 1 ? '' : 's'})`,
                    value: String(g.id),
                  })),
                ]}
                value={testGroupId}
                onChange={setTestGroupId}
                helpText={
                  !groups.data?.items.length
                    ? 'لسه مفيش groups. روح "إدارة Test Groups" واعمل واحدة.'
                    : undefined
                }
              />
            )}
          </BlockStack>
        </Modal.Section>
      </Modal>

      {/* ---- Save-as-template modal -------------------------------- */}
      <Modal
        open={saveTplOpen}
        onClose={() => setSaveTplOpen(false)}
        title="حفظ كـ template"
        primaryAction={{
          content: 'حفظ',
          onAction: submitSaveTemplate,
          loading: createTemplate.isPending,
        }}
        secondaryActions={[{ content: 'إلغاء', onAction: () => setSaveTplOpen(false) }]}
      >
        <Modal.Section>
          <BlockStack gap="300">
            {tplErr ? (
              <Banner tone="critical" onDismiss={() => setTplErr(null)}>
                <p>{tplErr}</p>
              </Banner>
            ) : null}
            <TextField
              label="اسم الـ template"
              autoComplete="off"
              value={tplName}
              onChange={setTplName}
              placeholder="مثل: عرض الجمعة الأسبوعي"
              maxLength={100}
              showCharacterCount
              requiredIndicator
              helpText="هتلاقيه في tab الـ Templates وتقدر تـ load محتواه بضغطة."
            />
          </BlockStack>
        </Modal.Section>
      </Modal>

      {/* ---- Groups manager modal ----------------------------------- */}
      <Modal
        open={groupManagerOpen}
        onClose={() => setGroupManagerOpen(false)}
        title="إدارة الـ Test Groups"
        primaryAction={{
          content: 'إضافة group جديد',
          icon: PlusIcon,
          onAction: openCreateGroup,
        }}
        secondaryActions={[{ content: 'إغلاق', onAction: () => setGroupManagerOpen(false) }]}
      >
        <Modal.Section>
          {groups.isLoading ? (
            <InlineStack align="center"><Spinner accessibilityLabel="loading" /></InlineStack>
          ) : (groups.data?.items ?? []).length === 0 ? (
            <Banner tone="info"><p>لسه مفيش groups. اعمل أول واحدة من زرار "إضافة group جديد".</p></Banner>
          ) : (
            <BlockStack gap="200">
              {(groups.data?.items ?? []).map((g: DeviceGroup) => (
                <Box
                  key={g.id}
                  background="bg-surface-secondary"
                  padding="300"
                  borderRadius="200"
                >
                  <InlineStack align="space-between" blockAlign="center">
                    <BlockStack gap="050">
                      <Text as="h3" variant="headingSm">{g.name}</Text>
                      <Text as="p" variant="bodySm" tone="subdued">
                        {g.member_count} device{g.member_count === 1 ? '' : 's'}
                        {g.members.length > 0
                          ? ` · ${g.members.slice(0, 3).map((m: { id: number }) => `#${m.id}`).join(', ')}${g.members.length > 3 ? '…' : ''}`
                          : ''}
                      </Text>
                    </BlockStack>
                    <InlineStack gap="100">
                      <Button icon={EditIcon} variant="tertiary" onClick={() => openEditGroup(g)}>
                        تعديل
                      </Button>
                      <Button
                        icon={DeleteIcon}
                        variant="tertiary"
                        tone="critical"
                        onClick={() => deleteGroup.mutate(g.id)}
                        loading={deleteGroup.isPending}
                      >
                        حذف
                      </Button>
                    </InlineStack>
                  </InlineStack>
                </Box>
              ))}
            </BlockStack>
          )}
        </Modal.Section>
      </Modal>

      {/* ---- Group create/edit form modal --------------------------- */}
      <Modal
        open={groupFormOpen}
        onClose={() => setGroupFormOpen(false)}
        title={editingGroup ? `تعديل: ${editingGroup.name}` : 'group جديد'}
        primaryAction={{
          content: editingGroup ? 'حفظ' : 'إنشاء',
          onAction: submitGroup,
          loading: createGroup.isPending || updateGroup.isPending,
        }}
        secondaryActions={[{ content: 'إلغاء', onAction: () => setGroupFormOpen(false) }]}
      >
        <Modal.Section>
          <BlockStack gap="300">
            {groupErr ? (
              <Banner tone="critical" onDismiss={() => setGroupErr(null)}>
                <p>{groupErr}</p>
              </Banner>
            ) : null}
            <TextField
              label="اسم الـ group"
              autoComplete="off"
              value={groupName}
              onChange={setGroupName}
              placeholder="مثل: فريق التيست"
              maxLength={100}
              showCharacterCount
            />
            <Divider />
            <Box>
              <Text as="p" variant="bodyMd" fontWeight="medium">
                Devices ({groupDeviceIds.size} مختار)
              </Text>
              <Text as="p" variant="bodySm" tone="subdued">
                اختار الأجهزة اللي تحب الـ notifications توصلها لما تـ test على الـ group ده.
              </Text>
            </Box>
            <Box
              borderColor="border"
              borderWidth="025"
              borderRadius="200"
              padding="200"
              maxWidth="100%"
            >
              <BlockStack gap="100">
                {(devices.data?.items ?? [])
                  .filter((d: DeviceItem) => !d.disabled)
                  .map((d: DeviceItem) => (
                    <Checkbox
                      key={d.id}
                      label={`#${d.id} · ${d.platform}/${d.locale ?? '?'} · ${
                        d.customer_id ? 'logged-in' : 'guest'
                      } · ${formatTime(d.last_seen)}`}
                      checked={groupDeviceIds.has(d.id)}
                      onChange={() => toggleDeviceInGroup(d.id)}
                    />
                  ))}
                {(devices.data?.items ?? []).filter((d: DeviceItem) => !d.disabled).length === 0 ? (
                  <Text as="p" tone="subdued">مفيش devices مسجلة لسه.</Text>
                ) : null}
              </BlockStack>
            </Box>
          </BlockStack>
        </Modal.Section>
      </Modal>
    </Page>
  );
}
